import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Lock, CheckCircle2, ShieldCheck, Sparkles, Gift, RefreshCw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ShareWinCard from "@/components/ShareWinCard";
import AttachmentsPanel from "@/components/AttachmentsPanel";

const STEPS = ["Draft ready","1st sent","2nd sent","3rd sent","4th sent","Resolved"];
const STATUS_TO_STEP = { draft_ready:0, sent_1:1, sent_2:2, sent_3:3, sent_4:4, resolved:5 };

export default function CaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [secondAttemptBusy, setSecondAttemptBusy] = useState(false);
  const [outcome, setOutcome] = useState({ outcome: "", outcome_amount: "", outcome_notes: "" });
  const [applyCredit, setApplyCredit] = useState(false);
  const [me, setMe] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = () => api.get(`/cases/${id}`).then((r) => setC(r.data)).catch((e) => {
    if (e?.response?.status === 404) setNotFound(true);
  });

  useEffect(() => {
    load().finally(() => setLoading(false));
    api.get("/referrals/me").then((r) => setMe(r.data)).catch(()=>{});
  }, [id]);

  useEffect(() => {
    if (c?.paid && !c?.messages?.["1"] && !generating) {
      setGenerating(true);
      api.post(`/cases/${id}/generate-messages`).then(load).catch(()=>{}).finally(() => setGenerating(false));
    }
  }, [c?.paid]);

  if (notFound) return (
    <div className="min-h-screen bg-[#FAFBFD]"><Nav />
      <div className="max-w-lg mx-auto p-12 text-center" data-testid="case-not-found">
        <h1 className="text-2xl font-bold text-slate-900">Case not found</h1>
        <p className="text-slate-500 mt-2">This case doesn't exist or you don't have access.</p>
        <button onClick={() => nav("/dashboard")} className="mt-4 rounded-full px-5 py-2 bg-[#1E3A8A] text-white hover:bg-[#1E40AF]" data-testid="not-found-back-button">Back to my cases</button>
      </div>
    </div>
  );

  if (loading || !c) return <div className="min-h-screen bg-[#FAFBFD]"><Nav /><div className="p-12 text-center text-slate-500">Loading…</div></div>;

  const currentStep = STATUS_TO_STEP[c.status] ?? 0;
  const progress = (currentStep / 5) * 100;

  const startCheckout = async () => {
    setCheckoutBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        case_id: c.id,
        origin_url: window.location.origin,
        apply_referral_credit: applyCredit,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setCheckoutBusy(false);
    }
  };

  const copyMsg = async (msg) => {
    const text = `Subject: ${msg.subject}\n\n${msg.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied! Ready to paste into email or the merchant's support form.");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Copied! Ready to paste into email or the merchant's support form.");
      } catch {
        toast.error("Couldn't copy — long-press the message and copy manually.");
      }
    }
  };

  const markSent = async (n) => {
    const r = await api.patch(`/cases/${c.id}`, { status: `sent_${n}` });
    setC(r.data);
    toast.success(`Marked Message ${n} as sent.`);
  };

  const toggleEvidence = async (idx) => {
    const r = await api.patch(`/cases/${c.id}/evidence/${idx}`);
    setC({ ...c, evidence_checklist: r.data.evidence_checklist });
  };

  const markResolved = async () => {
    const r = await api.patch(`/cases/${c.id}`, {
      status: "resolved",
      outcome: outcome.outcome,
      outcome_amount: outcome.outcome_amount ? parseFloat(outcome.outcome_amount) : undefined,
      outcome_notes: outcome.outcome_notes,
    });
    setC(r.data);
    toast.success("Case resolved. Nice work.");
  };

  const redeemFreeCase = async () => {
    setRedeeming(true);
    try {
      await api.post(`/cases/${c.id}/redeem-free-case`);
      // refresh user + case
      const meRes = await api.get("/auth/me");
      setUser(meRes.data);
      await load();
      toast.success("Free case redeemed! Drafting your messages…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem free case");
    } finally {
      setRedeeming(false);
    }
  };

  const startSecondAttempt = async () => {
    setSecondAttemptBusy(true);
    try {
      const r = await api.post(`/cases/${c.id}/second-attempt`);
      toast.success("Second attempt created — pay $9 to unlock");
      nav(`/case/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start second attempt");
    } finally {
      setSecondAttemptBusy(false);
    }
  };

  const messageNums = ["1","2","3","4"];
  const isSecondAttempt = !!c.is_second_attempt;
  const priceLabel = isSecondAttempt ? "$9" : "$19";
  const canRedeemFree = !isSecondAttempt && user?.free_case_available;

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-6">
          <button onClick={() => nav("/dashboard")} className="text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="back-to-dashboard-link">← All cases</button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{c.merchant_name}</h1>
              <p className="text-slate-600 mt-1">${c.amount?.toFixed(2)} charged {c.charge_date} · cancelled {c.cancel_date}</p>
            </div>
            <Badge variant="outline" className="rounded-full text-sm px-4 py-1.5 bg-white">
              {STEPS[currentStep]}
            </Badge>
          </div>
        </div>

        {/* Progress bar */}
        <Card className="trust-card mb-6" data-testid="case-progress-bar">
          <CardContent className="p-6">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
              {STEPS.map((s, i) => (
                <span key={s} className={i === currentStep ? "font-semibold text-[#1E3A8A]" : ""}>{s}</span>
              ))}
            </div>
            <Progress value={progress} className="h-2" />
          </CardContent>
        </Card>

        {/* Paywall or messages */}
        {!c.paid ? (
          <Card className="trust-card mb-6" data-testid="paywall-card">
            <CardContent className="p-8 text-center">
              {isSecondAttempt && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold mb-4" data-testid="second-attempt-badge">
                  <RefreshCw className="w-3.5 h-3.5" /> Second attempt · $10 off
                </div>
              )}
              <div className="w-14 h-14 mx-auto rounded-full bg-[#EEF4FF] text-[#1E3A8A] grid place-items-center mb-4">
                <Lock className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Unlock your 4 messages</h2>
              <p className="text-slate-600 mt-2 max-w-md mx-auto">
                {isSecondAttempt
                  ? "$9 (was $19). All 4 escalation messages, refreshed evidence checklist, fresh tracker."
                  : "One-time $19 fee. All 4 messages, evidence checklist, and case tracking."}
              </p>

              {canRedeemFree && (
                <div className="mt-6 p-5 rounded-2xl bg-emerald-50 border border-emerald-200 text-left max-w-md mx-auto" data-testid="free-case-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift className="w-5 h-5 text-emerald-700" />
                    <p className="font-semibold text-emerald-900">Your first case is on us</p>
                  </div>
                  <p className="text-sm text-emerald-800">You're one of our first 100 founders. Redeem your free case now — no card needed.</p>
                  <Button data-testid="redeem-free-case-button" onClick={redeemFreeCase} disabled={redeeming} className="mt-4 w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700">
                    {redeeming ? "Redeeming…" : "Use my free case"}
                  </Button>
                </div>
              )}

              {me?.credit_balance > 0 && !isSecondAttempt && (
                <label className="mt-4 inline-flex items-center gap-2 text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2" data-testid="referral-credit-toggle">
                  <Checkbox checked={applyCredit} onCheckedChange={(v)=>setApplyCredit(!!v)} />
                  Apply your ${me.credit_balance.toFixed(2)} referral credit
                </label>
              )}

              <div className="mt-6">
                <Button data-testid="stripe-paywall-checkout-button" onClick={startCheckout} disabled={checkoutBusy} size="lg" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-14 px-8 text-base font-semibold">
                  {checkoutBusy ? "Redirecting…" : `Pay ${priceLabel} & unlock messages`}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-4 max-w-md mx-auto flex items-center justify-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Non-refundable. We draft and guide — refunds are not guaranteed.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {generating || !c.messages?.["1"] ? (
              <Card className="trust-card mb-6">
                <CardContent className="p-8 text-center">
                  <Sparkles className="w-8 h-8 mx-auto text-[#1E3A8A] animate-pulse" />
                  <p className="mt-3 text-slate-600">Drafting your 4 messages…</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {messageNums.map((n) => {
                  const msg = c.messages[n];
                  if (!msg) return null;
                  const step = parseInt(n);
                  const isSent = currentStep >= step;
                  const isCurrent = currentStep === step - 1 || (currentStep === 0 && step === 1);
                  return (
                    <Card key={n} className={`trust-card ${isCurrent ? "ring-2 ring-[#93C5FD]" : ""}`} data-testid={`message-card-${n}`}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Message {n}</span>
                              {isSent && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 mt-1">{msg.title}</h3>
                            <p className="text-xs text-slate-500 mt-1">{msg.tone} · {msg.when}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button data-testid={`one-tap-copy-message-${n}`} onClick={() => copyMsg(msg)} variant="outline" className="rounded-full">
                              <Copy className="w-4 h-4 mr-2" /> Copy
                            </Button>
                            {!isSent && (
                              <Button data-testid={`mark-sent-message-${n}`} onClick={() => markSent(n)} className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]">
                                Mark as sent
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-700 border border-slate-200">
                          <p className="font-semibold mb-2 text-slate-900">Subject: {msg.subject}</p>
                          <pre className="whitespace-pre-wrap font-sans">{msg.body}</pre>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Evidence checklist */}
            <Card className="trust-card mt-6" data-testid="evidence-checklist-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-slate-900">Evidence checklist</h3>
                <p className="text-sm text-slate-500 mt-1">Attach these to your emails when relevant.</p>
                <div className="mt-4 space-y-2">
                  {c.evidence_checklist?.map((item, idx) => (
                    <label key={idx} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`evidence-item-${idx}`}>
                      <Checkbox checked={item.done} onCheckedChange={() => toggleEvidence(idx)} className="mt-0.5" />
                      <span className={`text-sm ${item.done ? "line-through text-slate-400" : "text-slate-700"}`}>{item.text}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <AttachmentsPanel caseId={c.id} />
                </div>
              </CardContent>
            </Card>

            {/* Resolved / outcome */}
            {c.status !== "resolved" && currentStep >= 1 && (
              <Card className="trust-card mt-6" data-testid="mark-resolved-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900">Got a resolution?</h3>
                  <p className="text-sm text-slate-500 mt-1">Log the outcome so you can move on.</p>
                  <div className="grid sm:grid-cols-3 gap-4 mt-4">
                    <div>
                      <Label>Outcome</Label>
                      <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={outcome.outcome} onChange={(e)=>setOutcome({...outcome, outcome: e.target.value})} data-testid="outcome-select">
                        <option value="">Choose…</option>
                        <option value="full_refund">Full refund</option>
                        <option value="partial_refund">Partial refund</option>
                        <option value="denied">Denied</option>
                        <option value="ignored">Ignored</option>
                      </select>
                    </div>
                    <div>
                      <Label>Amount received</Label>
                      <Input type="number" step="0.01" value={outcome.outcome_amount} onChange={(e)=>setOutcome({...outcome, outcome_amount: e.target.value})} placeholder="15.49" data-testid="outcome-amount-input" />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={markResolved} className="w-full h-10 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mark-resolved-button">
                        Mark resolved
                      </Button>
                    </div>
                  </div>
                  <Textarea className="mt-4" placeholder="Any notes? (e.g., who replied, what they said)" value={outcome.outcome_notes} onChange={(e)=>setOutcome({...outcome, outcome_notes: e.target.value})} data-testid="outcome-notes-input" />
                </CardContent>
              </Card>
            )}

            {c.status === "resolved" && (
              <>
                <Card className="trust-card mt-6 border-emerald-200 bg-emerald-50/40" data-testid="resolved-summary-card">
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mb-4">
                      <CheckCircle2 className="w-7 h-7" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Case resolved</h2>
                    <p className="text-slate-600 mt-2">
                      {c.outcome === "full_refund" && `Full refund received — $${(c.outcome_amount||c.amount).toFixed(2)}`}
                      {c.outcome === "partial_refund" && `Partial refund of $${c.outcome_amount?.toFixed(2)} received`}
                      {c.outcome === "denied" && "Refund was denied. Consider a chargeback with your bank."}
                      {c.outcome === "ignored" && "Merchant never responded. Consider a chargeback."}
                    </p>
                    {c.outcome_notes && <p className="text-sm text-slate-500 mt-3">"{c.outcome_notes}"</p>}
                  </CardContent>
                </Card>

                {(c.outcome === "full_refund" || c.outcome === "partial_refund") && (
                  <Card className="trust-card mt-6" data-testid="share-win-card">
                    <CardContent className="p-6 sm:p-8">
                      <h3 className="text-lg font-semibold text-slate-900 text-center">Share your win</h3>
                      <p className="text-sm text-slate-500 mt-1 text-center">A redacted timeline card — safe to post to TikTok or IG.</p>
                      <div className="mt-6">
                        <ShareWinCard caseData={c} />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {(c.outcome === "denied" || c.outcome === "ignored") && !c.is_second_attempt && (
                  <Card className="trust-card mt-6 border-amber-200 bg-amber-50/50" data-testid="second-attempt-cta-card">
                    <CardContent className="p-6 sm:p-8 text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 text-amber-700 grid place-items-center mb-3">
                        <RefreshCw className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900">Not done yet — try again for $9</h3>
                      <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">A second attempt with fresh drafts and a new escalation timeline. $10 off because your first attempt didn't stick.</p>
                      <Button onClick={startSecondAttempt} disabled={secondAttemptBusy} data-testid="start-second-attempt-button" className="mt-5 rounded-full bg-amber-600 hover:bg-amber-700 text-white h-11 px-6">
                        {secondAttemptBusy ? "Creating…" : "Start second attempt for $9"}
                      </Button>
                      <p className="text-xs text-slate-500 mt-4">
                        Or go straight to the last resort → <a href="/chargeback-guide" className="text-[#1E3A8A] hover:underline" data-testid="chargeback-guide-link">read the chargeback guide</a>
                      </p>
                    </CardContent>
                  </Card>
                )}

                {(c.outcome === "denied" || c.outcome === "ignored") && c.is_second_attempt && (
                  <Card className="trust-card mt-6 border-slate-200" data-testid="chargeback-cta-card">
                    <CardContent className="p-6 sm:p-8 text-center">
                      <h3 className="text-lg font-semibold text-slate-900">Time for the last resort</h3>
                      <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">You've done everything you can with {c.merchant_name}. File a chargeback with your card issuer — we'll walk you through it.</p>
                      <a href="/chargeback-guide" data-testid="chargeback-guide-button">
                        <Button className="mt-5 rounded-full bg-slate-900 hover:bg-slate-800 text-white h-11 px-6">
                          Open chargeback guide <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                )}

                <Card className="trust-card mt-6" data-testid="post-resolve-referral-card">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm text-slate-500">Refer a friend — give $10, get $10.</p>
                    <Button onClick={() => nav("/referrals")} variant="outline" className="mt-3 rounded-full" data-testid="share-win-button">
                      Get my referral link
                    </Button>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
